import { useTranslation } from "react-i18next";
import { Facebook, Instagram, Youtube } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import { getApplicationCanonicalPath } from "@/i18n/legacySeoPaths";
import gssaLogo from "@/assets/gssa-logo.png";

// Update these URLs to match your actual social media profiles
const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/thailandeliteagent",
  instagram: "https://www.instagram.com/thailand.elite/",
  youtube: "https://www.youtube.com/@thailandelitevisas",
};

const Footer = () => {
  const { t, i18n } = useTranslation();
  const applicationGuidePath = getApplicationCanonicalPath(i18n.language);

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <img src={gssaLogo} alt="GSSA Authorized Agent" className="w-[100px] h-[100px] object-contain opacity-90" width={100} height={100} loading="lazy" decoding="async" />
            <p className="text-xs text-primary-foreground/60 font-body leading-relaxed">
              {t("footer.authorized")}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-4 text-pagoda-gold">
              {t("footer.usefulLinks")}
            </h4>
            <ul className="space-y-2 font-body text-sm">
              <li><LocalizedLink to="/memberships" className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">{t("nav.memberships")}</LocalizedLink></li>
              <li><LocalizedLink to={applicationGuidePath} className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">{t("nav.howToApply")}</LocalizedLink></li>
              <li><LocalizedLink to="/about" className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">{t("nav.about")}</LocalizedLink></li>
              <li><LocalizedLink to="/faqs" className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">{t("nav.faqs")}</LocalizedLink></li>
              <li><LocalizedLink to="/blog" className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">{t("nav.blog", "Articles")}</LocalizedLink></li>
              <li><LocalizedLink to="/privacy" className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">{t("footer.privacy")}</LocalizedLink></li>
              <li><LocalizedLink to="/terms" className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">{t("footer.terms")}</LocalizedLink></li>
            </ul>
          </div>

          {/* Contact + Social icons */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-4 text-pagoda-gold">
              {t("footer.contact")}
            </h4>
            <ul className="space-y-2 font-body text-sm mb-6">
              <li>
                <LocalizedLink to="/contact" className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">
                  {t("footer.requestInfo")}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/apply" className="text-primary-foreground/70 hover:text-pagoda-gold transition-colors">
                  {t("footer.applyOnline")}
                </LocalizedLink>
              </li>
            </ul>

            {/* Social icons */}
            <p className="text-xs font-body font-semibold text-primary-foreground/50 uppercase tracking-widest mb-3">
              {t("footer.followUs")}
            </p>
            <div className="flex items-center gap-4">
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-primary-foreground/60 hover:text-pagoda-gold transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-primary-foreground/60 hover:text-pagoda-gold transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_LINKS.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="text-primary-foreground/60 hover:text-pagoda-gold transition-colors"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Trust badges */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-4 text-pagoda-gold">
              {t("footer.social")}
            </h4>
            <p className="text-sm font-body font-semibold text-pagoda-gold mb-3 italic">
              {t("footer.ratings")}
            </p>
            <div className="space-y-2 font-body text-sm text-primary-foreground/70">
              <p>✓ {t("trust.noExtra")}</p>
              <p>✓ {t("trust.experience")}</p>
              <p>✓ {t("trust.support")}</p>
              <p>✓ {t("trust.licensed")}</p>
              <p>✓ {t("trust.secure")}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/10 mt-12 pt-6 space-y-2">
          {/* GSSA attribution — establishes authorization to operate this
              channel without naming or linking the program operator (who is
              also a direct sales competitor). */}
          <p className="text-center text-xs text-primary-foreground/50 font-body">
            {t(
              "footer.gssaAttribution",
              "Authorized Global Sales & Services Agent (GSSA) for the Thailand Elite long-stay visa programme — Dreamond, License SA21/012.",
            )}
          </p>
          <p className="text-center text-xs text-primary-foreground/40 font-body">
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
